import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconPlus, IconAlertCircle, IconBuildingStore, IconChevronRight, IconUsers,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

interface ApiAccount {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  creditLimitCents: number;
  balanceCents: number;
  _count: { members: number };
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function AdminAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<ApiAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/api/accounts', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setAccounts(data.accounts ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <AdminLayout
      title="Accounts"
      subtitle="Internal multi-member groups (admin / staff / suppliers)"
      actions={
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
        >
          <IconPlus size={16} stroke={2.5} />
          New account
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-4">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
              <IconBuildingStore size={22} stroke={2} />
            </div>
            <p className="text-14 font-bold text-heading">No accounts yet</p>
            <p className="text-13 font-medium text-gray-700 mt-1">Create one for your admin team, kitchen staff, or any internal group.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Members</th>
                  <th className="py-2 pr-3 text-right">Balance</th>
                  <th className="py-2 pr-3 text-right">Credit limit</th>
                  <th className="py-2 pr-3 text-right">Available</th>
                  <th className="py-2 pr-3 text-center">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => {
                  const available = Math.max(0, a.creditLimitCents - a.balanceCents);
                  const overLimit = a.balanceCents >= a.creditLimitCents;
                  return (
                    <tr key={a.id} onClick={() => navigate(`/admin/accounts/${a.id}`)} className="border-t border-border-soft cursor-pointer hover:bg-light">
                      <td className="py-2.5 pr-3 font-bold text-heading">{a.name}</td>
                      <td className="py-2.5 pr-3 text-gray-700 inline-flex items-center gap-1.5">
                        <IconUsers size={13} stroke={2.25} />
                        {a._count.members}
                      </td>
                      <td className={`py-2.5 pr-3 text-right font-bold tabular-nums ${a.balanceCents > 0 ? 'text-warning' : 'text-heading'}`}>{fmtR(a.balanceCents)}</td>
                      <td className="py-2.5 pr-3 text-right text-gray-700 tabular-nums">{fmtR(a.creditLimitCents)}</td>
                      <td className={`py-2.5 pr-3 text-right tabular-nums ${overLimit ? 'text-danger font-bold' : 'text-success font-bold'}`}>{fmtR(available)}</td>
                      <td className="py-2.5 pr-3 text-center">
                        <span className={[
                          'inline-flex items-center px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider',
                          a.status === 'ACTIVE' ? 'bg-success-soft text-success' : 'bg-light text-gray-700',
                        ].join(' ')}>
                          {a.status}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <IconChevronRight size={16} stroke={2.25} className="text-gray-700" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(id) => { setCreateOpen(false); navigate(`/admin/accounts/${id}`); }}
      />
    </AdminLayout>
  );
}

function CreateAccountModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: (id: string) => void }) {
  const [name, setName] = useState('');
  const [limitRand, setLimitRand] = useState('1000.00');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setName(''); setLimitRand('1000.00'); setErr(null); } }, [open]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const r = await fetch('/api/accounts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          creditLimitCents: Math.round(Number(limitRand.replace(',', '.')) * 100),
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      const data = await r.json();
      onSaved(data.account.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm" title="New account">
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Name</span>
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Admin Team"
          className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
        />
      </label>
      <label className="block mb-4">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Credit limit (R)</span>
        <input
          type="text"
          inputMode="decimal"
          value={limitRand}
          onChange={(e) => setLimitRand(e.target.value.replace(/[^0-9.,]/g, ''))}
          className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading"
        />
      </label>
      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}
      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button onClick={save} disabled={saving || !name.trim()} className="flex-[2] h-12 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
          {saving ? 'Creating…' : 'Create account'}
        </button>
      </div>
    </Modal>
  );
}
